// src/components/CustomerEmailLogs.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Spinner, Alert, Pagination, TextInput, Button } from 'flowbite-react';
import api from "../../../../services/api/axiosConfig"
import API_ENDPOINTS from '../../../../services/api/endpoints';
import { useNavigate } from 'react-router-dom';

const CustomerEmailLogs = ({ customerId, customerPage = false }) => {

    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState({
        total: 0,
        per_page: 10,
        current_page: 1,
        last_page: 1,
        from: 0,
        to: 0,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [keywordInput, setKeywordInput] = useState(''); // State for the input field value
    const [appliedKeywordFilter, setAppliedKeywordFilter] = useState(''); // State for the filter applied to API
    const [seenCount, setSeenCount] = useState(0); // New state for seen count
    const [unseenCount, setUnseenCount] = useState(0); // New state for unseen count
    const navigate = useNavigate();

    const fetchEmailLogs = useCallback(async (filterKeyword) => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                page: pagination.current_page,
                per_page: pagination.per_page,
                customer_id: customerId,
            };

            // Add email filter to parameters if it's not empty
            if (filterKeyword) {
                params.keyword = filterKeyword;
            }

            const response = await api.get(API_ENDPOINTS.PANEL.CUSTOMER.MAIL_LISTING, { params });

            if (response.data.success) {
                const fetchedData = response.data.data;
                const fetchedPagination = response.data.pagination;

                setLogs(fetchedData);
                setPagination(fetchedPagination);

                // Calculate seen and unseen counts for the current page
                const currentSeenCount = fetchedData.filter(log => log.seen_at).length;
                const currentUnseenCount = fetchedData.filter(log => !log.seen_at).length;
                setSeenCount(currentSeenCount);
                setUnseenCount(currentUnseenCount);

            } else {
                setError(response.data.message || 'Failed to fetch email logs.');
            }
        } catch (err) {
            console.error('Error fetching email logs:', err);
            if (err.response) {
                setError(err.response.data.message || `Error: ${err.response.status} - ${err.response.statusText}`);
            } else if (err.request) {
                setError('Network error: No response from server.');
            } else {
                setError('An unexpected error occurred.');
            }
        } finally {
            setLoading(false);
        }
    }, [pagination.current_page, pagination.per_page, customerId]);

    // Effect to fetch logs when pagination, customerId, or the applied filter changes
    useEffect(() => {
        fetchEmailLogs(appliedKeywordFilter);
    }, [fetchEmailLogs, appliedKeywordFilter]);

    const onPageChange = (page) => {
        setPagination((prev) => ({ ...prev, current_page: page }));
    };

    const handleRowClick = (emailLogId) => {
        navigate(`/dashboard/customer-email-detail/${emailLogId}`);
    };

    // Handler for the "Search" button click
    const handleSearchClick = () => {
        // Reset to first page when a new filter is applied
        setPagination(prev => ({ ...prev, current_page: 1 }));
        setAppliedKeywordFilter(keywordInput); // Apply the filter from the input field
    };

    // Allow pressing Enter key to trigger search
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearchClick();
        }
    };

    return (
        <div className="p-4">
            {error && (
                <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
                    <span className="font-medium">Error!</span> {error}
                </Alert>
            )}

            {/* Email Filter Input and Search Button */}
            {customerPage === false && (
                <div className="mb-4 flex items-center gap-4">
                    <TextInput
                        id="keywordFilter"
                        type="text"
                        placeholder="Filter by email, subject"
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)} // Update input state directly
                        onKeyDown={handleKeyDown} // Listen for Enter key
                        className="flex-grow max-w-sm"
                    />
                    <Button onClick={handleSearchClick}>Search</Button> {/* Search Button */}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <Spinner size="xl" />
                </div>
            ) : (
                <div className="overflow-x-auto">
                    {logs.length > 0 ? (
                        <>
                            {/* Seen/Unseen Count Display */}
                            <div className="mb-4 text-sm text-gray-700 dark:text-gray-300">
                                 <span className="font-semibold text-red-600">Unread emails: {unseenCount}</span> | <span className="font-semibold text-green-600">Read emails: {seenCount}</span> 
                            </div>

                            <table className="w-full text-sm text-left rtl:text-right">
                                <thead className="text-xs uppercase bg-gray-100">
                                    <tr>
                                        {customerPage === false && (
                                            <th className="px-6 py-3">Customer Name</th>
                                        )}
                                        <th className="px-6 py-3">Email</th>
                                        <th className="px-6 py-3">Subject</th>
                                        <th className="px-6 py-3">Last Read At</th>
                                        <th className="px-6 py-3">Open times</th>
                                        <th className="px-6 py-3">Created At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log, index) => (
                                        <tr
                                            key={log.id}
                                            // Conditionally apply 'font-bold' class for unseen emails
                                            className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b dark:border-gray-700 hover:bg-gray-100 cursor-pointer ${!log.seen_at ? 'font-bold' : ''}`}
                                            onClick={() => handleRowClick(log.id)}
                                        >
                                            {customerPage === false && (
                                                <td className="px-6 py-4">{log.full_name} </td>
                                            )}
                                            <td className="px-6 py-4">{log.email} </td>
                                            <td className="px-6 py-4">{log.subject}</td>
                                            <td className="px-6 py-4">{log.seen_at ? new Date(log.seen_at).toLocaleString('en-US', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true,
                                            }) : '-'}</td>
                                            <td className="px-6 py-4">{log.open_times}</td>
                                            <td className="px-6 py-4">
                                                {new Date(log.created_at).toLocaleString('en-US', {
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    hour12: true,
                                                })}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    ) : (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            {customerPage ? 'No email logs found for this customer' : 'No email logs found.'}
                        </div>
                    )}
                </div>
            )}

            {/* Pagination */}
            {logs.length > 0 && (
                <>
                    <div className="flex overflow-x-auto sm:justify-center mt-6">
                        <Pagination
                            currentPage={pagination.current_page}
                            totalPages={pagination.last_page}
                            onPageChange={onPageChange}
                            showIcons
                        />
                    </div>
                    <div className="text-center mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Showing {pagination.from} to {pagination.to} of {pagination.total} entries
                    </div>
                </>
            )}
        </div>
    );
};

export default CustomerEmailLogs;